// Treasury withdraw-requests module endpoint functions.
//
// Endpoints accept camelCase view-model inputs and return camelCase view-model
// outputs. Snake_case lives ONLY between the network and the parser.

import { adminApi } from '@core/api/httpClient'
import { WITHDRAW_REQUESTS_ENDPOINT_PATHS } from './withdrawRequestsEndpointPaths'
import type {
  ApproveWithdrawRequestPayloadDto,
  MarkSettledWithdrawRequestPayloadDto,
  RejectWithdrawRequestPayloadDto,
  WithdrawRequestDetailDto,
  WithdrawRequestRowDto,
  WithdrawRequestStatusCountsDto,
  WithdrawRequestsListResponseDto,
} from './withdrawRequestsContracts'
import type {
  ApproveWithdrawRequestInput,
  GetWithdrawRequestsParams,
  MarkSettledWithdrawRequestInput,
  RejectWithdrawRequestInput,
  WithdrawRequestDetail,
  WithdrawRequestRow,
  WithdrawRequestStatusCounts,
  WithdrawRequestsListResponse,
} from '../types/withdrawRequestsTypes'
import {
  parseWithdrawRequestDetail,
  parseWithdrawRequestRow,
  parseWithdrawRequestStatusCounts,
  parseWithdrawRequestsListResponse,
} from './withdrawRequestsParser'

type WireListResponse = WithdrawRequestsListResponseDto | { data: WithdrawRequestsListResponseDto }
type WireDetailResponse = WithdrawRequestDetailDto | { data: WithdrawRequestDetailDto }
type WireRowResponse = WithdrawRequestRowDto | { data: WithdrawRequestRowDto }
type WireStatusCounts = WithdrawRequestStatusCountsDto | { data: WithdrawRequestStatusCountsDto }

function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export async function getWithdrawRequests(
  params: GetWithdrawRequestsParams,
): Promise<WithdrawRequestsListResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = String(params.page)
  if (params.limit) queryParams.limit = String(params.limit)
  if (params.status) queryParams.status = params.status
  if (params.fromDate) queryParams.fromDate = params.fromDate
  if (params.toDate) queryParams.toDate = params.toDate
  if (params.investorId) queryParams.investorId = params.investorId
  if (params.investorName) queryParams.investorName = params.investorName
  if (params.investorEmail) queryParams.investorEmail = params.investorEmail

  const { data } = await adminApi.get<WireListResponse>(WITHDRAW_REQUESTS_ENDPOINT_PATHS.LIST, {
    params: queryParams,
  })
  return parseWithdrawRequestsListResponse(unwrap(data))
}

export async function getWithdrawRequestStatusCounts(): Promise<WithdrawRequestStatusCounts> {
  const { data } = await adminApi.get<WireStatusCounts>(
    WITHDRAW_REQUESTS_ENDPOINT_PATHS.STATUS_COUNTS,
  )
  return parseWithdrawRequestStatusCounts(unwrap(data))
}

export async function getWithdrawRequestDetail(id: string): Promise<WithdrawRequestDetail> {
  const { data } = await adminApi.get<WireDetailResponse>(
    WITHDRAW_REQUESTS_ENDPOINT_PATHS.DETAIL(id),
  )
  return parseWithdrawRequestDetail(unwrap(data))
}

export async function approveWithdrawRequest(
  input: ApproveWithdrawRequestInput,
): Promise<WithdrawRequestRow> {
  const payload: ApproveWithdrawRequestPayloadDto = {
    treasuryBankAccountId: input.treasuryBankAccountId,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireRowResponse>(
    WITHDRAW_REQUESTS_ENDPOINT_PATHS.APPROVE(input.id),
    payload,
  )
  return parseWithdrawRequestRow(unwrap(data))
}

export async function markWithdrawRequestSettled(
  input: MarkSettledWithdrawRequestInput,
): Promise<WithdrawRequestRow> {
  const payload: MarkSettledWithdrawRequestPayloadDto = {
    settlementReference: input.settlementReference,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireRowResponse>(
    WITHDRAW_REQUESTS_ENDPOINT_PATHS.MARK_SETTLED(input.id),
    payload,
  )
  return parseWithdrawRequestRow(unwrap(data))
}

export async function rejectWithdrawRequest(
  input: RejectWithdrawRequestInput,
): Promise<WithdrawRequestRow> {
  const payload: RejectWithdrawRequestPayloadDto = {
    reason: input.reason,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireRowResponse>(
    WITHDRAW_REQUESTS_ENDPOINT_PATHS.REJECT(input.id),
    payload,
  )
  return parseWithdrawRequestRow(unwrap(data))
}
